import { Subject } from 'rxjs';
import { AppConsts } from '@shared/AppConsts';

/** Browser coordination only. Backend native sessions and resource authorization remain authoritative. */
export class PurlivoBrowserContext {
    static readonly invalidated = new Subject<void>();
    static stale = false;
    static surface = '';
    private static principal = '';
    private static started = false;
    private static connections = new Set<{ stop: () => Promise<void> }>();
    static configure(surface: string): void {
        if (surface !== 'host' && surface !== 'tenant') return;
        this.surface = surface;
        abp.auth.tokenCookieName = 'Purlivo.' + surface + '.AuthToken';
        abp.auth.refreshTokenCookieName = 'Purlivo.' + surface + '.AuthRefreshToken';
        // Keep the native HTTP header name while isolating the underlying cookie values across ports.
        const get = abp.utils.getCookieValue;
        const set = abp.utils.setCookieValue as (key: string, value: string, expires?: Date, path?: string,
            domain?: string, attributes?: Record<string, string | boolean>) => void;
        const remove = abp.utils.deleteCookie;
        const name = (key: string) => ['Abp-TenantId', 'abp_tenancy_name'].includes(key) ? 'Purlivo.' + surface + '.' + key : key;
        abp.utils.getCookieValue = key => get(name(key));
        abp.utils.setCookieValue = (key, value, expires, path, domain, attributes?: Record<string, string | boolean>) => {
            const mapped = name(key);
            const isolated = mapped.startsWith('Purlivo.' + surface + '.');
            // Keep authentication and tenant-selection cookies host-only; TLS cookies also require Secure.
            const flags = location.protocol === 'https:' ? { ...attributes, Secure: true, SameSite: 'Lax' } : attributes;
            set(mapped, value, expires, path, isolated ? undefined : domain, flags);
        };
        abp.utils.deleteCookie = (key, path) => remove(name(key), path);
    }
    static start(): void {
        if (!this.surface) return;
        if (this.started) { this.inspect(); return; }
        this.started = true; this.principal = this.currentPrincipal();
        const inspect = () => this.inspect();
        window.addEventListener('storage', inspect);
        window.addEventListener('focus', inspect);
        window.addEventListener('pageshow', inspect);
        window.addEventListener('online', inspect);
        document.addEventListener('visibilitychange', inspect);
        setInterval(inspect, 250);
    }
    static registerConnection(connection: { stop: () => Promise<void> }): void {
        if (this.stale) { void connection.stop(); return; }
        this.connections.add(connection);
    }
    static check(): boolean {
        this.inspect();
        return !this.stale;
    }
    private static inspect(): void {
        const current = this.currentPrincipal();
        // Login can complete within the existing document; bind before its first authenticated request.
        if (!this.principal && current && !this.stale) this.principal = current;
        if (this.principal && current !== this.principal) this.disable();
    }
    private static currentPrincipal(): string {
        try {
            const token = abp.auth.getToken();
            if (!token) return '';
            const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
            // Native UserIdentifier carries both account and tenant, stable across refreshes.
            const account = payload['user_identifier'] ??
                JSON.stringify([payload.sub, payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'], payload['http://www.aspnetboilerplate.com/identity/claims/tenantId']]);
            return JSON.stringify([account,
                payload['http://www.aspnetboilerplate.com/identity/claims/impersonatorUserId'] ?? null,
                payload['http://www.aspnetboilerplate.com/identity/claims/impersonatorTenantId'] ?? null]);
        } catch { return ''; }
    }
    private static disable(): void {
        if (this.stale) return;
        this.stale = true;
        this.invalidated.next();
        for (const connection of this.connections) void connection.stop().catch(() => undefined);
        // Remove old resource contents and controls. Reopening the new context requires an explicit click.
        const panel = document.createElement('main'); panel.className = 'container p-5'; panel.dataset['testid'] = 'purlivo-context-changed';
        const message = document.createElement('p'); message.textContent = abp.localization.localize('PurlivoContextChanged', 'UVSAN');
        const button = document.createElement('button'); button.className = 'btn btn-primary';
        button.textContent = abp.localization.localize('PurlivoReopenWorkspace', 'UVSAN');
        button.onclick = () => location.replace(AppConsts.appBaseUrl + '/app/main/purlivo');
        panel.append(message, button); document.body.replaceChildren(panel);
    }
}
